import React, { useState, useEffect } from 'react';
import {
    Box, Tabs, Tab, TextField, MenuItem, Select, FormControl, InputLabel, AppBar,
    Checkbox, FormControlLabel, Button, Card, CardContent, List, ListItem, ListItemText, IconButton,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const ApiSettingsForm = ({ item = {}, errors = {}, disabled = false, onChange, trackChanges = false, showSms = false }) => {
    const [tab, setTab] = useState(0);
    const [touchedFields, setTouchedFields] = useState({});
    const [alarmEmails, setAlarmEmails] = useState(item.api_alarm_emails ? item.api_alarm_emails.split(',') : []);
    const [errorEmails, setErrorEmails] = useState(item.api_error_emails ? item.api_error_emails.split(',') : []);
    const [newEmail, setNewEmail] = useState('');
    const [openEmailDialog, setOpenEmailDialog] = useState(false);
    const [emailType, setEmailType] = useState('');
    const [alarmPhone, setAlarmPhone] = useState('');
    const [alarmPhoneEnabled, setAlarmPhoneEnabled] = useState(false);
    const [errorPhone, setErrorPhone] = useState('');
    const [errorPhoneEnabled, setErrorPhoneEnabled] = useState(false);

    useEffect(() => {
        // Signal that the component has mounted and rendered
        window.dispatchEvent(new CustomEvent("apiSettingsForm:loaded"));
    }, []); // Empty dependency array ensures this runs only once on mount

    // Use useEffect to initialize alarmEmails and errorEmails when the item prop changes
    useEffect(() => {
        const section = (item[`api_alarm_settings`] || [])[0] || {};
        setAlarmEmails(section.emails ? section.emails.split(',') : []);
        setAlarmPhone(section.phone || '');
        setAlarmPhoneEnabled(!!section.phone_enabled);
    }, [item.api_alarm_settings]);

    useEffect(() => {
        const section = (item[`api_error_settings`] || [])[0] || {};
        setErrorEmails(section.emails ? section.emails.split(',') : []);
        setErrorPhone(section.phone || '');
        setErrorPhoneEnabled(!!section.phone_enabled);
    }, [item.api_error_settings]);

    useEffect(() => {
        if (tab !== 4) {
            return;
        }

        const enhancedMethodField = document.getElementById('input-api_enhanced_analyzer_http_method');
        if (enhancedMethodField && !enhancedMethodField.value) {
            enhancedMethodField.value = 'GET';
        }

        const enhancedFormatField = document.getElementById('input-api_enhanced_analyzer_text_format');
        if (enhancedFormatField && !enhancedFormatField.value) {
            enhancedFormatField.value = 'json';
        }
    }, [tab]);

    const handleTabChange = (_, newValue) => setTab(newValue);

    const markTouched = (field) => {
        if (trackChanges) {
            setTouchedFields((prev) => ({ ...prev, [field]: true }));
        }
    };

    const getApiSection = (type) => {
        const section = (item[`${type}_settings`] || [])[0] || {};
        return {
            url: section.url || '',
            http_method: section.http_method || 'GET',
            text_format: section.additional_info?.format || 'json',
            emails: section.emails || [],
            emails_enabled: section.emails_enabled || false,
            enabled: section.enabled || false,
            phone: section.phone || '',
            phone_enabled: section.phone_enabled || false,
        };
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        markTouched(name);
        if (onChange) {
            onChange({ name, value: type === 'checkbox' ? checked : value });
        }
    };

    const handleEmailChange = (emails, type) => {
        if (onChange) {
            onChange({ name: `api_${type}_emails`, value: emails.join(',') });
        }
    };

    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isValidRomanianPhone = (phone) => {
        return /^\+40\d{9}$/.test(phone);
    };

    const handleAddEmail = (type) => {
        setEmailType(type);
        setOpenEmailDialog(true);
        setNewEmail('');
    };

    const handleCloseEmailDialog = () => {
        setOpenEmailDialog(false);
    };

    const handleSaveEmail = () => {
        if (isValidEmail(newEmail)) {
            if (emailType === 'alarm') {
                setAlarmEmails([...alarmEmails, newEmail]);
                handleEmailChange([...alarmEmails, newEmail], 'alarm');
            } else if (emailType === 'error') {
                setErrorEmails([...errorEmails, newEmail]);
                handleEmailChange([...errorEmails, newEmail], 'error');
            }
            setOpenEmailDialog(false);
        } else {
            alert('Please enter a valid email address.');
        }
    };

    const deleteEmail = (index, type) => {
        if (type === 'alarm') {
            const newEmails = [...alarmEmails];
            newEmails.splice(index, 1);
            setAlarmEmails(newEmails);
            handleEmailChange(newEmails, 'alarm');
        } else if (type === 'error') {
            const newEmails = [...errorEmails];
            newEmails.splice(index, 1);
            setErrorEmails(newEmails);
            handleEmailChange(newEmails, 'error');
        }
    };

    const handlePhoneChange = (e, type) => {
        const value = e.target.value;
        markTouched(`${type}_phone`);
        if (type === 'alarm') {
            setAlarmPhone(value);
        } else if (type === 'error') {
            setErrorPhone(value);
        }
        if (onChange) {
            onChange({ name: `api_${type}_phone`, value });
        }
    };

    const handlePhoneEnabledChange = (e, type) => {
        const checked = e.target.checked;
        markTouched(`${type}_phone_enabled`);
        if (type === 'alarm') {
            setAlarmPhoneEnabled(checked);
        } else if (type === 'error') {
            setErrorPhoneEnabled(checked);
        }
        if (onChange) {
            onChange({ name: `api_${type}_phone_enabled`, value: checked });
        }
    };

    const renderTextField = (name, label, value) => (
        <TextField
            fullWidth
            margin="normal"
            name={name}
            id={`input-${name}`}
            label={label}
            defaultValue={value}
            onChange={handleChange}
            disabled={disabled}
            error={!!errors[name]}
            helperText={errors[name]}
            sx={trackChanges && touchedFields[name] ? { borderLeft: '4px solid #1976d2', pl: 1 } : {}}
        />
    );

    const renderSelect = (name, label, value, options) => (
        <FormControl fullWidth margin="normal" error={!!errors[name]} sx={trackChanges && touchedFields[name] ? { borderLeft: '4px solid #1976d2', pl: 1 } : {}}>
            <InputLabel id={`${name}-label`}>{label}</InputLabel>
            <Select
                labelId={`${name}-label`}
                name={name}
                id={`input-${name}`}
                defaultValue={value}
                onChange={handleChange}
                disabled={disabled}
            >
                {options.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const renderToggle = (name, label, checked) => (
        <FormControlLabel
            control={
                <Checkbox
                    name={name}
                    id={name}
                    defaultChecked={checked}
                    onChange={handleChange}
                    disabled={disabled}
                />
            }
            label={label}
            sx={trackChanges && touchedFields[name] ? { borderLeft: '4px solid #1976d2', pl: 1 } : {}}
        />
    );

    const renderSection = (index, prefix, label, emails = [], setEmails) => {
        const data = getApiSection(prefix);
        const isAlarmOrError = label === 'Alarm' || label === 'Error';

        let phone = '';
        let phoneEnabled = false;
        let phoneError = '';
        if (label === 'Alarm') {
            phone = alarmPhone;
            phoneEnabled = alarmPhoneEnabled;
            phoneError = phone && !isValidRomanianPhone(phone) ? 'Invalid Romanian phone (+40xxxxxxxxx)' : '';
        } else if (label === 'Error') {
            phone = errorPhone;
            phoneEnabled = errorPhoneEnabled;
            phoneError = phone && !isValidRomanianPhone(phone) ? 'Invalid Romanian phone (+40xxxxxxxxx)' : '';
        }

        return (
            <Box sx={{ display: tab === index ? 'block' : 'none', mt: 2 }}>
                {renderTextField(`${prefix}_url`, `${label} URL`, data.url)}
                {renderSelect(`${prefix}_http_method`, `${label} HTTP Method`, data.http_method, ['POST', 'GET'])}
                {renderSelect(`${prefix}_text_format`, `Text Format`, data.text_format, ['json', 'text'])}
                {renderToggle(`${prefix}_enabled`, 'Enabled', !!data.enabled)}

                {isAlarmOrError && (
                    <>
                        {showSms && (<TextField
                            fullWidth
                            margin="normal"
                            name={`${prefix}_phone`}
                            id={`input-${prefix}_phone`}
                            label="Phone (+40xxxxxxxxx)"
                            value={phone}
                            onChange={(e) => handlePhoneChange(e, prefix.split('_')[1])}
                            disabled={disabled}
                            error={!!phoneError}
                            helperText={phoneError}
                            sx={trackChanges && touchedFields[`${prefix}_phone`] ? { borderLeft: '4px solid #1976d2', pl: 1 } : {}}
                        />)}
                        {showSms && (<FormControlLabel
                            control={
                                <Checkbox
                                    name={`${prefix}_phone_enabled`}
                                    id={`${prefix}_phone_enabled`}
                                    checked={!!phoneEnabled}
                                    onChange={(e) => handlePhoneEnabledChange(e, prefix.split('_')[1])}
                                    disabled={disabled}
                                />
                            }
                            label="Phone Enabled"
                            sx={trackChanges && touchedFields[`${prefix}_phone_enabled`] ? { borderLeft: '4px solid #1976d2', pl: 1 } : {}}
                        />)}
                        <input type="hidden"
                               name={`${prefix}_emails`}
                            id={`input-${prefix}_emails`}
                            value={emails.join(', ')} // Display emails as comma-separated string
                        />
                        <Box sx={{ mt: 2 }}>
                            <InputLabel>Emails</InputLabel>
                            <List dense>
                                {emails.map((email, index) => (
                                    <ListItem
                                        key={index}
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete"  disabled={disabled} onClick={() => deleteEmail(index, prefix.split('_')[1])}>
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText primary={email} />
                                    </ListItem>
                                ))}
                            </List>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={disabled}
                                onClick={() => handleAddEmail(prefix.split('_')[1])}
                            >
                                Add Email
                            </Button>
                        </Box>
                        {renderToggle(`${prefix}_emails_enabled`, 'Emails Enabled',  !!data.emails_enabled)}
                    </>
                )}

                <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => window.testApi(`${label}API`)}
                >
                    Test
                </Button>
            </Box>
        );
    };

    return (
        <Card variant="outlined" sx={{ width: '80%', mt: 2 }}>
            <CardContent>
                <AppBar position="static" color="transparent" elevation={0}>
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    textColor="inherit"
                    indicatorColor="secondary"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label="Alarms" value={0} />
                    <Tab label="Errors" value={1} />
                    <Tab label="Analyzers" value={2} />
                    <Tab label="Counters" value={3} />
                    <Tab label="Enhanced" value={4} />
                    <Tab label="Record" value={5} />
                </Tabs>
                </AppBar>

                {renderSection(0, 'api_alarm', 'Alarm', alarmEmails)}
                {renderSection(1, 'api_error', 'Error', errorEmails)}
                {renderSection(2, 'api_analyzer', 'Analyzer')}
                {renderSection(3, 'api_counter', 'Counter')}
                {renderSection(4, 'api_enhanced_analyzer', 'EnhancedAnalyzer')}
                {renderSection(5, 'api_record', 'Record')}

                <Dialog open={openEmailDialog} onClose={handleCloseEmailDialog}>
                    <DialogTitle>Add Email</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Please enter a valid email address.
                        </DialogContentText>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="email"
                            label="Email Address"
                            type="email"
                            fullWidth
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseEmailDialog}>Cancel</Button>
                        <Button onClick={handleSaveEmail}>Save</Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
};

export default ApiSettingsForm;
